import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchMilestones,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  toggleMilestoneComplete,
} from '../api/milestonesApi'

export default function MilestoneManager({ projectId }) {
  const queryClient = useQueryClient()
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_date: '',
  })

  const { data: milestones = [], isLoading } = useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => fetchMilestones(projectId),
    enabled: !!projectId,
  })

  const createMutation = useMutation({
    mutationFn: createMilestone,
    onSuccess: () => {
      queryClient.invalidateQueries(['milestones', projectId])
      setIsAdding(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => updateMilestone(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['milestones', projectId])
      setEditingId(null)
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteMilestone,
    onSuccess: () => {
      queryClient.invalidateQueries(['milestones', projectId])
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isCompleted }) => toggleMilestoneComplete(id, isCompleted),
    onSuccess: () => {
      queryClient.invalidateQueries(['milestones', projectId])
    },
  })

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      target_date: '',
    })
  }

  const handleAdd = () => {
    setIsAdding(true)
    setEditingId(null)
    resetForm()
  }

  const handleEdit = (milestone) => {
    setEditingId(milestone.id)
    setIsAdding(false)
    setFormData({
      title: milestone.title,
      description: milestone.description || '',
      target_date: milestone.target_date || '',
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!formData.title.trim()) return

    const milestoneData = {
      project_id: projectId,
      title: formData.title.trim(),
      description: formData.description.trim(),
      target_date: formData.target_date || null,
      display_order: editingId ? undefined : milestones.length,
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, updates: milestoneData })
    } else {
      createMutation.mutate(milestoneData)
    }
  }

  const handleCancel = () => {
    setIsAdding(false)
    setEditingId(null)
    resetForm()
  }

  const handleDelete = (id) => {
    if (confirm('Are you sure you want to delete this milestone?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleToggle = (milestone) => {
    toggleMutation.mutate({
      id: milestone.id,
      isCompleted: !milestone.is_completed,
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'No date set'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-secondary-200 rounded w-1/3" />
          <div className="h-20 bg-secondary-100 rounded" />
          <div className="h-20 bg-secondary-100 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="card p-6">
      <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className="text-lg font-semibold text-secondary-900">Milestones</h4>
              <p className="text-sm text-secondary-600 mt-1">
                Track and share your project progress
              </p>
            </div>
            {!isAdding && !editingId && (
              <button onClick={handleAdd} className="btn-primary">
                Add Milestone
              </button>
            )}
          </div>

      {(isAdding || editingId) && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-secondary-50 rounded-lg">
          <div className="space-y-4">
            <div>
              <label className="label">Milestone Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input"
                placeholder="e.g., Resource Consent Approved"
                required
              />
            </div>

            <div>
              <label className="label">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input"
                rows="2"
                placeholder="Optional details about this milestone"
              />
            </div>

            <div>
              <label className="label">Target Date</label>
              <input
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                className="input"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="btn-primary"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : editingId
                  ? 'Update Milestone'
                  : 'Create Milestone'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {milestones.length === 0 ? (
        <div className="text-center py-8 text-secondary-600">
          <p>No milestones yet. Add your first milestone to start tracking progress.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {milestones.map((milestone) => (
            <div
              key={milestone.id}
              className={`p-4 rounded-lg border-2 transition-colors ${
                milestone.is_completed
                  ? 'bg-green-50 border-green-200'
                  : 'bg-white border-secondary-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <button
                  onClick={() => handleToggle(milestone)}
                  className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors mt-0.5 ${
                    milestone.is_completed
                      ? 'bg-green-500 border-green-500'
                      : 'border-secondary-300 hover:border-primary-500'
                  }`}
                  disabled={toggleMutation.isPending}
                >
                  {milestone.is_completed && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold ${
                    milestone.is_completed ? 'text-green-900 line-through' : 'text-secondary-900'
                  }`}>
                    {milestone.title}
                  </h4>
                  {milestone.description && (
                    <p className="text-sm text-secondary-600 mt-1">{milestone.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-sm text-secondary-600">
                    {milestone.is_completed ? (
                      <span className="text-green-700">
                        Completed: {formatDate(milestone.completed_date)}
                      </span>
                    ) : milestone.target_date ? (
                      <span>Target: {formatDate(milestone.target_date)}</span>
                    ) : (
                      <span>No target date</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(milestone)}
                    className="text-primary-600 hover:text-primary-700 p-1"
                    title="Edit milestone"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(milestone.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                    disabled={deleteMutation.isPending}
                    title="Delete milestone"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  )
}
